import type { App } from "@upsquad/server";
import type { AddUnavailableDatePayload, SetUnavailabilityPayload, SetWeeklySchedulePayload } from "@upsquad/shared";
import type { EdenCall } from "@/src/common/lib/eden-base-query";
import { baseApi } from "@/src/common/services/base-api";

type GetAvailabilityResponse = App["~Routes"]["member"][":communityId"][":userId"]["availability"]["get"]["response"]["200"];
type SetWeeklyScheduleResponse = App["~Routes"]["member"][":communityId"]["me"]["availability"]["put"]["response"]["200"];
type AddUnavailableDateResponse = App["~Routes"]["member"][":communityId"]["me"]["unavailable-dates"]["post"]["response"]["200"];
type DeleteUnavailableDateResponse =
	App["~Routes"]["member"][":communityId"]["me"]["unavailable-dates"][":dateId"]["delete"]["response"]["200"];
type SetMyUnavailabilityResponse = App["~Routes"]["user"]["me"]["unavailability"]["patch"]["response"]["200"];

interface GetAvailabilityParams {
	communityId: string;
	userId: string;
}

interface SetWeeklyScheduleParams {
	communityId: string;
	payload: SetWeeklySchedulePayload;
}

interface AddUnavailableDateParams {
	communityId: string;
	payload: AddUnavailableDatePayload;
}

interface DeleteUnavailableDateParams {
	communityId: string;
	dateId: string;
}

const memberAvailabilityApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getAvailability: builder.query<GetAvailabilityResponse, GetAvailabilityParams>({
			query:
				({ communityId, userId }): EdenCall<GetAvailabilityResponse> =>
				(client) =>
					client.member({ communityId })({ userId }).availability.get(),
			providesTags: (_result, _error, { userId }) => [{ type: "Availability", id: userId }],
		}),
		setWeeklySchedule: builder.mutation<SetWeeklyScheduleResponse, SetWeeklyScheduleParams>({
			query:
				({ communityId, payload }) =>
				(client) =>
					client.member({ communityId }).me.availability.put(payload),
			invalidatesTags: ["Availability"],
		}),
		addUnavailableDate: builder.mutation<AddUnavailableDateResponse, AddUnavailableDateParams>({
			query:
				({ communityId, payload }) =>
				(client) =>
					client.member({ communityId }).me["unavailable-dates"].post(payload),
			invalidatesTags: ["Availability"],
		}),
		deleteUnavailableDate: builder.mutation<DeleteUnavailableDateResponse, DeleteUnavailableDateParams>({
			query:
				({ communityId, dateId }) =>
				(client) =>
					client.member({ communityId }).me["unavailable-dates"]({ dateId }).delete(),
			invalidatesTags: ["Availability"],
		}),
		setMyUnavailability: builder.mutation<SetMyUnavailabilityResponse, SetUnavailabilityPayload>({
			query:
				(payload): EdenCall<SetMyUnavailabilityResponse> =>
				(client) =>
					client.user.me.unavailability.patch(payload),
			invalidatesTags: ["MemberDetail"],
		}),
	}),
});

export const {
	useGetAvailabilityQuery,
	useSetWeeklyScheduleMutation,
	useAddUnavailableDateMutation,
	useDeleteUnavailableDateMutation,
	useSetMyUnavailabilityMutation,
} = memberAvailabilityApi;
